/**
 * Página de DTEs Recibidos.
 * Lista, filtros, upload, acciones (aceptar/rechazar/importar).
 */
import { useState, useRef, useEffect } from 'react'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import { PageHeader } from '@/components/HelpPanel'
import { HELP } from '@/data/helpContent'
import {
  Search, Upload, FileCheck, FileX, Download, CheckCircle, XCircle,
  AlertTriangle, Loader2, FileText, Inbox, Trash2, RotateCcw, PackageCheck,
} from 'lucide-react'
import apiClient from '@/lib/apiClient'
import { usePermission } from '@/hooks/usePermission'
import {
  useInboundDTEs, useInboundDTEStats, useAcuseReciboDTE, useAcceptDTE, useRejectDTE,
  useImportDTE, useUploadDTE, useDiscardDTE, useRestoreDTE, siiEventFlags,
  type InboundDTE,
} from '../hooks/useInboundDTE'
import { ApproveWithAccountModal } from '@/modules/accounting/components/ApproveWithAccountModal'
import { nonBillableAmount } from '@/modules/purchases/hooks/usePurchases'
import { EmptyState } from '@/components/EmptyState'
import { TableSkeleton } from '@/components/Skeleton'
import { PageSizeSelector, DEFAULT_PAGE_SIZE } from '@/components/PageSizeSelector'
import {
  type SortState, sortStateToParam,
  loadSortFromStorage, saveSortToStorage,
} from '@/components/SortableHeader'
import { DataTable } from '@/components/ds/DataTable'
import { ordenControlado } from '@/components/ds/ordenControlado'
import { Pagination } from '@/components/ds/Pagination'
import { useTenantLocale } from '@/lib/locale'
import { Modal } from '@/components/ds/Modal'

async function downloadDTEAsset(id: string, kind: 'pdf' | 'xml', filename: string) {
  const res = await apiClient.get(`/dte-inbound/${id}/${kind}/`, { responseType: 'blob' })
  const blobUrl = URL.createObjectURL(res.data)
  const a = document.createElement('a')
  a.href = blobUrl
  a.download = filename
  document.body.appendChild(a); a.click(); a.remove()
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1000)
}

const DTE_TYPE_LABELS: Record<number, string> = {
  33: 'Factura', 34: 'Factura Exenta', 39: 'Boleta', 41: 'Boleta Exenta',
  46: 'FC Compra', 52: 'Guía Despacho', 56: 'Nota Débito', 61: 'Nota Crédito',
}

const STATUS_COLORS: Record<string, string> = {
  received: 'bg-surface-muted text-content',
  parsed: 'bg-primary-100 text-primary-hover',
  validated: 'bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300',
  imported: 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300',
  accepted: 'bg-success-surface-strong text-success-content',
  rejected: 'bg-danger-surface-strong text-danger-content',
  error: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300',
}

export function InboundDTEPage() {
  const locale = useTenantLocale()
  // Tarjeta/segmento activo. 'pending' (pendientes por importar) es el default.
  const [cardFilter, setCardFilter] = useState<string>('pending')
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [showDiscarded, setShowDiscarded] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [sort, setSort] = useState<SortState>(() =>
    loadSortFromStorage('inbound-dte-sort', { field: 'fecha_emision', direction: 'desc' }),
  )
  const [selectedDTE, setSelectedDTE] = useState<InboundDTE | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const handleSortChange = (next: SortState) => {
    setSort(next); saveSortToStorage('inbound-dte-sort', next); setPage(1)
  }
  const handleCardClick = (key: string) => { setCardFilter(key); setPage(1) }

  const params: Record<string, string | number> = { page, page_size: pageSize }
  const ord = sortStateToParam(sort)
  if (ord) params.ordering = ord
  if (search) params.search = search
  if (typeFilter) params.tipo_dte = typeFilter
  if (showDiscarded) {
    // Vista de descartados: se muestran todos los descartados, sin filtro de estado.
    params.discarded = 'true'
  } else if (cardFilter === 'pending') {
    params.pending_import = 'true'
  } else if (cardFilter) {
    params.status = cardFilter
  }

  const { can } = usePermission()
  const canDiscard = can('dte_inbound', 'delete')

  const { data, isLoading } = useInboundDTEs(params)
  const dtes = data?.items ?? []
  const { data: stats } = useInboundDTEStats()
  const uploadMut = useUploadDTE()
  const acuseMut = useAcuseReciboDTE()
  const acceptMut = useAcceptDTE()
  const rejectMut = useRejectDTE()
  const importMut = useImportDTE()
  const discardMut = useDiscardDTE()
  const restoreMut = useRestoreDTE()

  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null)
  const [importTargetId, setImportTargetId] = useState<string | null>(null)
  // Evento positivo pendiente de confirmar (comparte el modal de cuenta porque
  // ambos auto-importan a Compras): 'acuse' = ERM, 'accept' = ACD.
  const [posEvent, setPosEvent] = useState<{ id: string; kind: 'acuse' | 'accept' } | null>(null)
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null)
  const [pdfLoading, setPdfLoading] = useState(false)
  const { confirm: confirmAction, modalProps } = useConfirmModal()

  // Cargar PDF como blob (con JWT) cuando se abre el modal con un DTE que tenga XML.
  // Usamos blob URL para que el iframe lo renderice inline sin chocar con X-Frame-Options
  // ni perder el header Authorization (los iframes no leen localStorage).
  useEffect(() => {
    if (!selectedDTE?.has_xml) {
      setPdfBlobUrl(null)
      return
    }
    let revoked = false
    let currentBlobUrl: string | null = null
    setPdfLoading(true)
    apiClient.get(`/dte-inbound/${selectedDTE.id}/pdf/`, { responseType: 'blob' })
      .then(res => {
        if (revoked) return
        currentBlobUrl = URL.createObjectURL(res.data)
        setPdfBlobUrl(currentBlobUrl)
      })
      .catch(() => { if (!revoked) setPdfBlobUrl(null) })
      .finally(() => { if (!revoked) setPdfLoading(false) })
    return () => {
      revoked = true
      if (currentBlobUrl) URL.revokeObjectURL(currentBlobUrl)
      setPdfBlobUrl(null)
    }
  }, [selectedDTE?.id, selectedDTE?.has_xml])

  const showToast = (msg: string, type: 'success' | 'error') => {
    setToast({ msg, type })
    setTimeout(() => setToast(null), 4000)
  }

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    uploadMut.mutate(file, {
      onSuccess: (data) => showToast(data.message || 'XML procesado.', 'success'),
      onError: (err: any) => showToast(err?.response?.data?.error?.message || 'Error.', 'error'),
    })
    e.target.value = ''
  }

  // Acuse de recibo (ERM) y aceptar contenido (ACD): ambos auto-importan a
  // Compras, por eso comparten el modal de selección de cuenta.
  const handleAcuse = (id: string) => setPosEvent({ id, kind: 'acuse' })
  const handleAccept = (id: string) => setPosEvent({ id, kind: 'accept' })

  const confirmPosEvent = (accountId: string | undefined) => {
    if (!posEvent) return
    const { id, kind } = posEvent
    const mut = kind === 'acuse' ? acuseMut : acceptMut
    const msg = kind === 'acuse' ? 'Acuse de recibo (ERM) en proceso.' : 'Aceptación (ACD) en proceso.'
    mut.mutate({ id, accountId }, {
      onSuccess: () => { showToast(msg, 'success'); setPosEvent(null) },
      onError: (err: any) => showToast(err?.response?.data?.error?.message || 'Error.', 'error'),
    })
  }

  const handleReject = (id: string) => {
    confirmAction({
      message: '¿Rechazar (reclamar al SII) este DTE? Es excluyente con el acuse de recibo y la aceptación (Ley 19.983).',
      variant: 'danger',
      confirmLabel: 'Rechazar',
      onConfirm: () => {
        rejectMut.mutate({ id }, {
          onSuccess: () => showToast('Rechazo (reclamo RCD) en proceso.', 'success'),
          onError: (err: any) => showToast(err?.response?.data?.error?.message || 'Error.', 'error'),
        })
      },
    })
  }

  const handleImport = (id: string) => {
    setImportTargetId(id)
  }

  const confirmImport = (accountId: string | undefined) => {
    if (!importTargetId) return
    importMut.mutate({ id: importTargetId, accountId }, {
      onSuccess: (data) => {
        showToast(data.message || 'DTE importado.', 'success')
        setImportTargetId(null)
      },
      onError: (err: any) => showToast(err?.response?.data?.error?.message || 'Error.', 'error'),
    })
  }

  const handleDiscard = (id: string) => {
    confirmAction({
      message: 'Se descartará este DTE del listado. No se borra el registro recibido y se puede restaurar después desde "Ver descartados". ¿Continuar?',
      variant: 'danger',
      confirmLabel: 'Descartar',
      onConfirm: () => {
        discardMut.mutate({ id }, {
          onSuccess: () => { showToast('DTE descartado.', 'success'); setSelectedDTE(null) },
          onError: (err: any) => showToast(err?.response?.data?.error?.message || 'Error.', 'error'),
        })
      },
    })
  }

  const handleRestore = (id: string) => {
    restoreMut.mutate(id, {
      onSuccess: () => { showToast('DTE restaurado.', 'success'); setSelectedDTE(null) },
      onError: (err: any) => showToast(err?.response?.data?.error?.message || 'Error.', 'error'),
    })
  }

  const fmtMonto = (val: string | null) => {
    if (!val) return '-'
    return '$' + Number(val).toLocaleString('es-CL')
  }

  /** Monto no facturable (cartolas de corredora / servicios periódicos): cuando
   *  el total tributario viene en 0, mostramos el monto real (MontoNF/Periodo/
   *  Exento) con la etiqueta "No facturable", igual que en /purchases/invoices. */
  const nonBillableFor = (dte: InboundDTE): string | null => {
    const total = dte.monto_total
    if (total != null && parseFloat(total) !== 0) return null
    return nonBillableAmount(dte.sii_montos)
  }

  return (
    <div className="p-6 space-y-6">
      {toast && (
        <div className={`fixed top-4 right-4 z-[100] px-4 py-2 rounded-lg text-sm shadow-lg ${
          toast.type === 'success' ? 'bg-success-fill text-white' : 'bg-danger-fill text-white'
        }`}>{toast.msg}</div>
      )}

      {/* Header */}
      <PageHeader
        title="DTEs Recibidos"
        subtitle="Documentos tributarios recibidos de proveedores."
        help={HELP['purchases.received-dtes']}
      >
        <button
          onClick={() => fileRef.current?.click()}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-on-primary rounded-lg text-sm hover:bg-primary-hover"
        >
          <Upload className="w-4 h-4" /> Subir XML
        </button>
        <input ref={fileRef} type="file" accept=".xml" className="hidden" onChange={handleUpload} />
      </PageHeader>

      {/* Tarjetas de resumen navegables: al clicar filtran la tabla. */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { key: 'pending',  label: 'Pendientes', value: stats.received + stats.parsed + stats.validated, color: 'text-cyan-600 dark:text-cyan-300' },
            { key: 'imported', label: 'Importados', value: stats.imported, color: 'text-indigo-600 dark:text-indigo-300' },
            { key: 'accepted', label: 'Aceptados',  value: stats.accepted, color: 'text-success-content-soft' },
            { key: 'rejected', label: 'Rechazados', value: stats.rejected, color: 'text-danger-content-soft' },
            { key: 'error',    label: 'Errores',    value: stats.error,    color: 'text-orange-600 dark:text-orange-300' },
            { key: '',         label: 'Total',      value: stats.total,    color: 'text-content' },
          ].map(s => (
            <button
              key={s.key || 'total'}
              onClick={() => handleCardClick(s.key)}
              className={`bg-surface rounded-lg border p-3 text-center transition-colors hover:bg-soft
                ${cardFilter === s.key ? 'border-primary ring-1 ring-primary' : 'border-divider'}`}
            >
              <div className={`text-lg font-bold ${s.color}`}>{s.value}</div>
              <div className="text-xs text-content-muted">{s.label}</div>
            </button>
          ))}
        </div>
      )}

      {/* Filtros (el estado lo gobiernan las tarjetas de arriba) */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-subtle" />
          <input aria-label="Buscar por RUT, razón social o folio"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1) }}
            placeholder="Buscar por RUT, razón social o folio..."
            className="w-full pl-9 pr-3 py-2 border border-input-border bg-input-bg text-content rounded-lg text-sm"
          />
        </div>
        <select aria-label="Todos los tipos"
          value={typeFilter}
          onChange={e => { setTypeFilter(e.target.value); setPage(1) }}
          className="border border-input-border bg-input-bg text-content rounded-lg px-3 py-2 text-sm"
        >
          <option value="">Todos los tipos</option>
          <option value="33">33 — Factura</option>
          <option value="34">34 — Factura Exenta</option>
          <option value="46">46 — Factura de Compra</option>
          <option value="56">56 — Nota de Débito</option>
          <option value="61">61 — Nota de Crédito</option>
          <option value="52">52 — Guía de Despacho</option>
        </select>
        {canDiscard && (
          <button
            type="button"
            onClick={() => { setShowDiscarded(v => !v); setPage(1) }}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border transition-colors ${
              showDiscarded ? 'border-primary text-primary bg-primary-100/40' : 'border-input-border text-content-muted hover:bg-soft'
            }`}
            title="Ver los DTEs descartados"
          >
            <Trash2 className="w-4 h-4" /> {showDiscarded ? 'Ver activos' : 'Ver descartados'}
          </button>
        )}
      </div>

      {/* Tabla */}
      <DataTable<typeof dtes[number]>
        columns={[
          { key: 'tipo_dte', header: 'Tipo',
            render: dte => (
              <span className="font-medium text-content whitespace-nowrap">
                {dte.tipo_dte ? DTE_TYPE_LABELS[dte.tipo_dte] || `Tipo ${dte.tipo_dte}` : '-'}
              </span>
            ) },
          // 🔴 Las `key` ordenables son los nombres que el backend espera en
          // `?ordering=`: esta tabla la pagina el SERVIDOR.
          { key: 'folio', header: 'Folio', sortable: true,
            render: dte => <span className="text-content-muted">{dte.folio || '-'}</span> },
          { key: 'razon_social_emisor', header: 'Emisor',
            render: dte => (
              <div className="max-w-[16rem]" title={dte.razon_social_emisor}>
                <div className="font-medium text-content truncate">{dte.razon_social_emisor || '-'}</div>
                <div className="text-xs text-content-subtle">{dte.rut_emisor}</div>
              </div>
            ) },
          { key: 'fecha_emision', header: 'Fecha', sortable: true,
            render: dte => (
              <span className="text-content-muted whitespace-nowrap">{dte.fecha_emision || '-'}</span>
            ) },
          { key: 'monto_total', header: 'Monto', numeric: true, sortable: true,
            render: dte => {
              // Un monto NO FACTURABLE no suma al total tributario, así que se
              // muestra rotulado: sin eso se lee como un monto más.
              const nf = nonBillableFor(dte)
              if (nf) return (
                <>
                  {fmtMonto(nf)}
                  <div className="text-[10px] text-content-subtle font-normal mt-0.5"
                    title="Monto no facturable (no suma al total tributario)">No facturable</div>
                </>
              )
              return fmtMonto(dte.monto_total)
            } },
          { key: 'validacion', header: 'Validación', width: 110,
            render: dte => (
              <div className="flex justify-center gap-1">
                {dte.xml_valid === true && <span title="XML OK"><FileCheck className="w-4 h-4 text-success-solid" /></span>}
                {dte.xml_valid === false && <span title="XML inválido"><FileX className="w-4 h-4 text-danger-solid" /></span>}
                {dte.signature_valid === true && <span title="Firma OK"><CheckCircle className="w-4 h-4 text-success-solid" /></span>}
                {dte.signature_valid === false && <span title="Firma inválida"><AlertTriangle className="w-4 h-4 text-yellow-500" /></span>}
              </div>
            ) },
          { key: 'status', header: 'Estado',
            render: dte => (
              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[dte.status] || 'bg-surface-muted'}`}>
                {dte.status_display}
              </span>
            ) },
          { key: 'acciones', header: 'Acciones', width: 190,
            render: dte => (
              <div className="flex justify-end gap-1" onClick={e => e.stopPropagation()}>
                {dte.has_xml && (
                  <button
                    type="button"
                    onClick={() => downloadDTEAsset(dte.id, 'pdf', `DTE-${dte.tipo_dte}-${dte.folio}.pdf`)}
                    className="p-1 text-content-subtle hover:text-primary inline-flex items-center"
                    title="Descargar PDF"
                  >
                    <FileText className="w-4 h-4" />
                  </button>
                )}
                {(dte.status === 'validated' || dte.status === 'parsed' || dte.status === 'accepted') && dte.tipo_dte !== 52 && (
                  <button onClick={() => handleImport(dte.id)} className="p-1 text-content-subtle hover:text-indigo-600 dark:text-indigo-300" title="Importar">
                    <Download className="w-4 h-4" />
                  </button>
                )}
                {!showDiscarded && dte.forma_pago === 2 && (() => {
                  const f = siiEventFlags(dte)
                  const positiveState = ['validated', 'imported', 'accepted'].includes(dte.status)
                  const canAcuse = positiveState && !f.hasReclamo && !f.hasErm
                  const canAccept = positiveState && !f.hasReclamo && !f.hasAcd
                  const canReject = ['validated', 'imported'].includes(dte.status) && !f.positive && !f.hasReclamo
                  return (
                    <>
                      {canAcuse && (
                        <button onClick={() => handleAcuse(dte.id)} className="p-1 text-content-subtle hover:text-success-content-soft" title="Acuse de recibo de mercaderías (ERM) — despeja el RCV">
                          <PackageCheck className="w-4 h-4" />
                        </button>
                      )}
                      {canAccept && (
                        <button onClick={() => handleAccept(dte.id)} className="p-1 text-content-subtle hover:text-success-content-soft" title="Aceptar contenido (ACD)">
                          <CheckCircle className="w-4 h-4" />
                        </button>
                      )}
                      {canReject && (
                        <button onClick={() => handleReject(dte.id)} className="p-1 text-content-subtle hover:text-danger-content-soft" title="Rechazar / reclamar (RCD)">
                          <XCircle className="w-4 h-4" />
                        </button>
                      )}
                    </>
                  )
                })()}
                {canDiscard && !showDiscarded && (
                  <button onClick={() => handleDiscard(dte.id)} className="p-1 text-content-subtle hover:text-danger-content-soft" title="Descartar del listado">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                {canDiscard && showDiscarded && (
                  <button onClick={() => handleRestore(dte.id)} className="p-1 text-content-subtle hover:text-primary" title="Restaurar al listado">
                    <RotateCcw className="w-4 h-4" />
                  </button>
                )}
              </div>
            ) },
        ]}
        rows={dtes}
        rowKey={dte => dte.id}
        minWidth={1180}
        loading={isLoading}
        loadingNode={<TableSkeleton rows={8} cols={8} />}
        onRowClick={dte => setSelectedDTE(dte)}
        {...ordenControlado(sort, handleSortChange)}
        empty={
          <EmptyState icon={Inbox} size="md" title="No hay DTEs"
            hint={showDiscarded ? 'No hay DTEs descartados.' : cardFilter === 'pending' ? 'No hay DTEs pendientes por importar.' : 'No hay DTEs con este filtro.'} />
        }
        caption="Documentos tributarios recibidos de proveedores"
      />

      {/* Paginador */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <PageSizeSelector value={pageSize} onChange={n => { setPageSize(n); setPage(1) }} storageKey="inbound-dte-page-size" />
        {data && data.pages > 1 && (
          <Pagination
            page={page}
            pageSize={pageSize}
            total={data.count}
            onPageChange={setPage}
            locale={locale}
          />
        )}
      </div>

      <ConfirmModal {...modalProps} />

      <ApproveWithAccountModal
        open={!!importTargetId}
        onClose={() => setImportTargetId(null)}
        isPending={importMut.isPending}
        title="Importar DTE"
        description="Al importar se crea una Factura de Proveedor y un comprobante contable en borrador. Por defecto se usa la cuenta del producto / proveedor / configuración. Se puede elegir otra cuenta de gasto."
        confirmLabel="Importar"
        onConfirm={confirmImport}
      />

      <ApproveWithAccountModal
        open={!!posEvent}
        onClose={() => setPosEvent(null)}
        isPending={acuseMut.isPending || acceptMut.isPending}
        title={posEvent?.kind === 'acuse' ? 'Acuse de recibo (ERM) e importar' : 'Aceptar contenido (ACD) e importar'}
        description={posEvent?.kind === 'acuse'
          ? 'El acuse de recibo de mercaderías (ERM) se registra en el SII (Ley 19.983), despeja la pestaña "Pendientes" del RCV y da mérito ejecutivo al documento. Además se importa a Compras (crea/converge la Factura de Proveedor y su comprobante). Se puede elegir la cuenta de gasto.'
          : 'La aceptación del contenido (ACD) se registra en el SII (Ley 19.983). Además se importa a Compras (crea/converge la Factura de Proveedor y su comprobante). Se puede elegir la cuenta de gasto.'}
        confirmLabel={posEvent?.kind === 'acuse' ? 'Acuse de recibo e importar' : 'Aceptar e importar'}
        onConfirm={confirmPosEvent}
      />

      {/* Detail Modal */}
      {selectedDTE && (
        <Modal open onClose={() => setSelectedDTE(null)} title={<>DTE {selectedDTE.tipo_dte ? DTE_TYPE_LABELS[selectedDTE.tipo_dte] || selectedDTE.tipo_dte : ''} #{selectedDTE.folio}</>} size="2xl">
          <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-2 gap-0">
            {/* Metadata + acciones */}
            <div className="overflow-y-auto px-6 py-5 space-y-4 text-sm border-r border-divider">
              <div className="grid grid-cols-2 gap-4">
                <div><span className="text-content-muted">Emisor:</span> {selectedDTE.razon_social_emisor}</div>
                <div><span className="text-content-muted">RUT:</span> {selectedDTE.rut_emisor}</div>
                <div><span className="text-content-muted">Fecha:</span> {selectedDTE.fecha_emision}</div>
                {(() => {
                  const nf = nonBillableFor(selectedDTE)
                  return nf
                    ? <div><span className="text-content-muted">No facturable:</span> <span className="text-warning-content-soft font-medium">{fmtMonto(nf)}</span></div>
                    : <div><span className="text-content-muted">Monto:</span> {fmtMonto(selectedDTE.monto_total)}</div>
                })()}
                <div><span className="text-content-muted">Origen:</span> {selectedDTE.source === 'email' ? 'Email' : 'Upload'}</div>
                <div><span className="text-content-muted">Estado:</span>
                  <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${STATUS_COLORS[selectedDTE.status]}`}>
                    {selectedDTE.status_display}
                  </span>
                </div>
                <div><span className="text-content-muted">Forma de pago:</span> {selectedDTE.forma_pago === 1 ? 'Contado' : selectedDTE.forma_pago === 2 ? 'Crédito' : selectedDTE.forma_pago === 3 ? 'Sin Costo' : '-'}</div>
              </div>

              <div className="border border-divider rounded-lg p-3 space-y-2">
                <h3 className="font-medium">Validación</h3>
                <div className="flex gap-4">
                  <ValidBadge label="XML" value={selectedDTE.xml_valid} />
                  <ValidBadge label="Firma" value={selectedDTE.signature_valid} />
                  <ValidBadge label="TED" value={selectedDTE.ted_valid} />
                </div>
              </div>

              {selectedDTE.error_message && (
                <div className="bg-danger-surface text-danger-content rounded-lg p-3 text-xs">{selectedDTE.error_message}</div>
              )}

              {selectedDTE.has_xml && (
                <div className="flex gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => downloadDTEAsset(selectedDTE.id, 'xml', `DTE-${selectedDTE.tipo_dte}-${selectedDTE.folio}.xml`)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-content border border-divider rounded-lg hover:bg-soft"
                  >
                    <Download className="w-4 h-4" /> Descargar XML
                  </button>
                  <button
                    type="button"
                    onClick={() => downloadDTEAsset(selectedDTE.id, 'pdf', `DTE-${selectedDTE.tipo_dte}-${selectedDTE.folio}.pdf`)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-content border border-divider rounded-lg hover:bg-soft"
                  >
                    <FileText className="w-4 h-4" /> Descargar PDF
                  </button>
                </div>
              )}

              {/* Eventos SII registrados (Ley 19.983) */}
              {selectedDTE.sii_events && selectedDTE.sii_events.length > 0 && (
                <div className="border border-divider rounded-lg p-3 space-y-1.5">
                  <h3 className="font-medium text-sm">Eventos SII (Ley 19.983)</h3>
                  {selectedDTE.sii_events.map(ev => (
                    <div key={ev.id} className="flex items-center justify-between text-xs gap-2">
                      <span className="text-content">{ev.tipo_display}</span>
                      <span className={ev.sii_ok ? 'text-success-content-soft' : 'text-warning-content-soft'}>
                        {ev.sii_ok ? 'Registrado en SII' : 'Pendiente / Error'}{ev.sii_fecha_evento ? ` · ${ev.sii_fecha_evento}` : ''}{ev.sent_to_emisor_at ? ' · enviado al emisor' : ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Acciones SII (solo crédito) */}
              {selectedDTE.forma_pago === 2 && (() => {
                const f = siiEventFlags(selectedDTE)
                const positiveState = ['validated', 'imported', 'accepted'].includes(selectedDTE.status)
                const canAcuse = positiveState && !f.hasReclamo && !f.hasErm
                const canAccept = positiveState && !f.hasReclamo && !f.hasAcd
                const canReject = ['validated', 'imported'].includes(selectedDTE.status) && !f.positive && !f.hasReclamo
                if (!canAcuse && !canAccept && !canReject) return null
                return (
                  <div className="border-t border-divider pt-4 space-y-2">
                    <h3 className="font-medium text-sm">Acciones SII (Ley 19.983)</h3>
                    <p className="text-xs text-content-muted">
                      El <strong>acuse de recibo (ERM)</strong> despeja la pestaña "Pendientes" del RCV; la <strong>aceptación (ACD)</strong> aprueba el contenido; el <strong>reclamo (RCD)</strong> rechaza el documento. Acuse y aceptación no son excluyentes; el reclamo sí.
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      {canAcuse && (
                        <button
                          onClick={() => { handleAcuse(selectedDTE.id); setSelectedDTE(null) }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-success-fill hover:bg-success-fill-hover rounded-lg"
                        >
                          <PackageCheck className="w-4 h-4" /> Acuse de recibo
                        </button>
                      )}
                      {canAccept && (
                        <button
                          onClick={() => { handleAccept(selectedDTE.id); setSelectedDTE(null) }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-success-fill hover:bg-success-fill-hover rounded-lg"
                        >
                          <CheckCircle className="w-4 h-4" /> Aceptar
                        </button>
                      )}
                      {canReject && (
                        <button
                          onClick={() => { handleReject(selectedDTE.id); setSelectedDTE(null) }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-danger-fill hover:bg-danger-fill-hover rounded-lg"
                        >
                          <XCircle className="w-4 h-4" /> Rechazar
                        </button>
                      )}
                    </div>
                  </div>
                )
              })()}

              {/* Descartar / restaurar */}
              {canDiscard && (
                <div className="border-t border-divider pt-4">
                  {selectedDTE.is_discarded ? (
                    <button
                      onClick={() => handleRestore(selectedDTE.id)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-content border border-divider rounded-lg hover:bg-soft"
                    >
                      <RotateCcw className="w-4 h-4" /> Restaurar al listado
                    </button>
                  ) : (
                    <button
                      onClick={() => handleDiscard(selectedDTE.id)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-danger-content-soft border border-danger-border rounded-lg hover:bg-danger-surface"
                    >
                      <Trash2 className="w-4 h-4" /> Descartar
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* PDF inline viewer */}
            <div className="hidden lg:flex flex-col bg-surface-muted">
              {!selectedDTE.has_xml ? (
                <div className="flex-1 flex items-center justify-center text-content-muted text-sm">
                  Sin XML disponible — no se puede generar el PDF.
                </div>
              ) : pdfLoading ? (
                <div className="flex-1 flex items-center justify-center text-content-muted text-sm">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" /> Generando PDF…
                </div>
              ) : pdfBlobUrl ? (
                <iframe
                  src={pdfBlobUrl}
                  title="DTE PDF"
                  className="flex-1 w-full border-0"
                />
              ) : (
                <div className="flex-1 flex items-center justify-center text-content-muted text-sm">
                  No se pudo cargar el PDF.
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

function ValidBadge({ label, value }: { label: string; value: boolean | null }) {
  if (value === null) return <span className="text-xs text-content-subtle">{label}: -</span>
  return (
    <span className={`text-xs ${value ? 'text-success-content-soft' : 'text-danger-content-soft'}`}>
      {value ? <CheckCircle className="w-3.5 h-3.5 inline mr-1" /> : <XCircle className="w-3.5 h-3.5 inline mr-1" />}
      {label}
    </span>
  )
}
